import { Eye, EyeOff, Layers } from 'lucide-react';
import { cn } from '../../utils/cn';
import { formatCount } from '../../utils/logbook';
import {
  LOGBOOK_GROUP_WINDOWS, LOGBOOK_NOISE_CLASSES, toggleNoiseClass,
  type LogbookPartition, type LogbookViewOptions,
} from '../../utils/logbookView';

interface LogbookViewBarProps {
  value: LogbookViewOptions;
  onChange: (next: LogbookViewOptions) => void;
  partition: LogbookPartition;
  /** How many runs the visible records collapsed into, so the saving is visible. */
  runCount?: number;
  className?: string;
}

/**
 * What the list is holding back, and how it is collapsed.
 *
 * The counts are the point. Hiding noise without saying how much was hidden turns a quiet page into
 * a claim that nothing happened, which on an evidence page is the wrong kind of quiet. Each class is
 * one click from visible, and the label says what it is rather than just how many.
 */
export const LogbookViewBar = ({ value, onChange, partition, runCount, className }: LogbookViewBarProps) => {
  const collapsed = runCount !== undefined && runCount > 0 ? partition.visible.length - runCount : 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="eyebrow mr-0.5">Hidden</span>
        {LOGBOOK_NOISE_CLASSES.map((noise) => {
          const shown = value.shown.includes(noise.id);
          const count = partition.hidden[noise.id];
          // A class with nothing to hide on this page stays available but reads as inactive.
          return (
            <button
              key={noise.id}
              type="button"
              onClick={() => onChange(toggleNoiseClass(value, noise.id))}
              className={cn('chip gap-1.5', shown && 'border-accent-400/50 bg-accent-500/15 text-white')}
              title={`${noise.description} ${shown ? 'Currently shown.' : 'Currently hidden.'}`}
              aria-pressed={!shown}
            >
              {shown ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {noise.label}
              {!shown && count > 0 && <span className="tabular-nums text-glass-400">{formatCount(count)}</span>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {collapsed > 0 && (
          <span className="flex items-center gap-1.5 text-xs text-glass-500 tabular-nums" title="Repeated records folded into runs">
            <Layers className="w-3.5 h-3.5" />
            {formatCount(collapsed)} folded
          </span>
        )}
        <select
          value={value.groupWindowMs}
          onChange={(event) => onChange({ ...value, groupWindowMs: Number(event.target.value) })}
          className="glass-select text-xs"
          aria-label="Group repeated records"
        >
          {LOGBOOK_GROUP_WINDOWS.map((window) => (
            <option key={window.ms} value={window.ms}>{window.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
};
